import React, { useState, useEffect, useRef, useMemo } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { loadStripe } from '@stripe/stripe-js';
import { Elements, CardElement, useStripe, useElements } from '@stripe/react-stripe-js';
import { useCart } from '../context/CartContext';
import { useSite } from '../context/SiteContext';
import { api } from '../services/api';
import { computeLineEstimate, lineEstimateLabel, formatMoney, isPackageMode, isBulkMode, computeBulkLineTotal, unitLabel } from '../utils/weightEstimate';
import './Checkout.css';

// Publishable key (platform). The Stripe.js instance is created INSIDE the
// Checkout component, memoized on the tenant's connected-account id, because the
// storefront's PaymentIntents are created ON the tenant connected account
// (siteCheckoutService.createPaymentIntent passes { stripeAccount }). Stripe.js
// MUST therefore be initialized with the SAME { stripeAccount } or the inline
// stripe.confirmCardPayment() looks for the PaymentIntent on the PLATFORM
// account, can't find it, and fails with "No such payment_intent" (Feature C
// C-4 fix). Connected-account ids are safe to expose client-side by design.
const STRIPE_PUBLISHABLE_KEY =
  process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY || 'pk_test_placeholder';

// Fallback sales-tax rate used only when the tenant has not configured one.
// (Was previously hardcoded throughout — now sourced from the tenant via
// SiteContext.taxRate. See Feature C C-1 audit F-C1-3 / D6.)
const FALLBACK_TAX_RATE = 0.0825;

// Legacy flat shipping fallback for any cart item whose shipping_zone has no
// configured rate. MUST match the server's LEGACY_FLAT_SHIPPING in
// sitePublicCheckout.js so the displayed total equals the charged total (C-4.3).
const LEGACY_FLAT_SHIPPING = 10.0;

// Split-cart bucket predicate. MUST mirror the server's partition in
// sitePublicCheckout.js resolveAndSplitCart(): sold_by === 'package' is the
// packaged-weight (price-at-weighing) bucket; 'each' and 'bulk' are deterministic
// charge-now. The server re-fetches sold_by authoritatively at checkout.
function isPackagedItem(item) {
  return isPackageMode(item);
}

function deterministicLineTotal(item) {
  if (isBulkMode(item)) {
    return computeBulkLineTotal(item, item.quantity) ?? 0;
  }
  return Number(item.price) * item.quantity;
}

function deterministicLineTax(item) {
  if (!item.is_taxable) return 0;
  const subtotal = deterministicLineTotal(item);
  const rate = Number(item.tax_rate) || 0;
  return Math.round(subtotal * rate * 100) / 100;
}

// US states for the delivery address selector (was Texas-only — F-C1-5).
const US_STATES = [
  ['AL','Alabama'],['AK','Alaska'],['AZ','Arizona'],['AR','Arkansas'],['CA','California'],
  ['CO','Colorado'],['CT','Connecticut'],['DE','Delaware'],['FL','Florida'],['GA','Georgia'],
  ['HI','Hawaii'],['ID','Idaho'],['IL','Illinois'],['IN','Indiana'],['IA','Iowa'],
  ['KS','Kansas'],['KY','Kentucky'],['LA','Louisiana'],['ME','Maine'],['MD','Maryland'],
  ['MA','Massachusetts'],['MI','Michigan'],['MN','Minnesota'],['MS','Mississippi'],['MO','Missouri'],
  ['MT','Montana'],['NE','Nebraska'],['NV','Nevada'],['NH','New Hampshire'],['NJ','New Jersey'],
  ['NM','New Mexico'],['NY','New York'],['NC','North Carolina'],['ND','North Dakota'],['OH','Ohio'],
  ['OK','Oklahoma'],['OR','Oregon'],['PA','Pennsylvania'],['RI','Rhode Island'],['SC','South Carolina'],
  ['SD','South Dakota'],['TN','Tennessee'],['TX','Texas'],['UT','Utah'],['VT','Vermont'],
  ['VA','Virginia'],['WA','Washington'],['WV','West Virginia'],['WI','Wisconsin'],['WY','Wyoming'],
  ['DC','District of Columbia'],
];

// Card styling options
const cardStyle = {
  style: {
    base: {
      color: '#333',
      fontFamily: '"Lato", sans-serif',
      fontSize: '16px',
      fontSmoothing: 'antialiased',
      '::placeholder': {
        color: '#aab7c4',
      },
    },
    invalid: {
      color: '#dc3545',
      iconColor: '#dc3545',
    },
  },
};

/**
 * Highest-rate-wins shipping resolver. MUST mirror resolveShipping() in
 * backend/src/routes/sitePublicCheckout.js exactly so the storefront's
 * displayed shipping equals what the server authoritatively charges (C-4.3 /
 * OD-2). pickup => 0. Otherwise each item's shipping_zone maps to a tenant
 * rate; the max across items is the charge (one shipment). An unconfigured or
 * null zone rate falls back to the legacy flat rate.
 *
 * Only DETERMINISTIC (charge-now) lines participate — the server resolves
 * shipping over the deterministic bucket only, and packaged-weight lines are
 * not charged at checkout, so they must not influence the displayed shipping.
 *
 * @param {string} deliveryMethod  'pickup' | 'delivery'
 * @param {Array<{shipping_zone?:string}>} items  deterministic cart items
 * @param {object} shippingRates   tenant rates by zone (SiteContext.shippingRates)
 * @returns {number} shipping charge in dollars (2dp)
 */
function resolveShipping(deliveryMethod, items, shippingRates) {
  if (deliveryMethod === 'pickup') return 0;
  const rates = shippingRates || {};
  let max = 0;
  for (const item of items) {
    const zone = item.shipping_zone || 'no-restrictions';
    let rate = rates[zone];
    if (rate === undefined || rate === null) {
      rate = LEGACY_FLAT_SHIPPING;
    }
    rate = Number(rate);
    if (Number.isFinite(rate) && rate > max) max = rate;
  }
  return Math.round(max * 100) / 100;
}

// Checkout Form Component
//
// deliveryMethod is OWNED BY THE PARENT (Checkout) and passed down with
// onDeliveryMethodChange so the order-summary shipping (parent) and the form's
// delivery selection stay in sync — a single source of truth (no dual state).
//
// hasDeterministic / chargeTotal are computed by the parent from the
// deterministic bucket. When hasDeterministic is false the cart is
// packaged-only: there is nothing to charge now, so the card field is hidden,
// the submit button is a plain "Place Order", and the server returns no
// client_secret (the inline confirm step is skipped).
//
// hasPackaged is true when the cart contains any package (billed-later) line.
// items are weighed after ordering, then billed via an emailed payment link)
// before the order can be placed. The consent is client-side only — it gates
// the submit button and is NOT persisted server-side (a server-recorded consent
// would be a separate, migration-bearing change).
function CheckoutForm({
  cartItems, hasDeterministic, hasPackaged, chargeTotal,
  deliveryMethod, onDeliveryMethodChange, site, onSuccess,
}) {
  const stripe = useStripe();
  const elements = useElements();
  const navigate = useNavigate();

  const [error, setError] = useState(null);
  const [processing, setProcessing] = useState(false);
  const [succeeded, setSucceeded] = useState(false);

  // Pre-order terms consent. Only meaningful when hasPackaged (package lines).
  const [acceptedPreorder, setAcceptedPreorder] = useState(false);

  // Idempotency key for POST /site/checkout. Generated once per "live" cart and
  // reused across retries of the same submission so a retry after a recoverable
  // error replays the same cart_group/PaymentIntent (m147) rather than creating
  // a duplicate. Reset on success so a brand-new order gets a fresh key.
  const idempotencyKeyRef = useRef(null);
  const getIdempotencyKey = () => {
    if (!idempotencyKeyRef.current) {
      const rand = (typeof crypto !== 'undefined' && crypto.randomUUID)
        ? crypto.randomUUID()
        : `${Date.now()}-${Math.random().toString(36).slice(2)}`;
      idempotencyKeyRef.current = `co-${rand}`;
    }
    return idempotencyKeyRef.current;
  };

  const pickupAddress = site.contactInfo?.address || '';
  const businessName = site.siteName || '';

  const [formData, setFormData] = useState({
    email: '',
    firstName: '',
    lastName: '',
    phone: '',
    address: '',
    city: '',
    state: '',
    zipCode: '',
    deliveryNotes: '',
  });

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  // Delivery method is parent-owned; the radio updates the parent.
  const handleDeliveryMethodChange = (e) => {
    onDeliveryMethodChange(e.target.value);
  };

  const isDelivery = deliveryMethod === 'delivery';

  // The order may be submitted unless a packaged cart's pre-order terms are
  // still unaccepted. (Each-only carts have no consent gate.)
  const consentSatisfied = !hasPackaged || acceptedPreorder;

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Guard: a packaged cart requires the pre-order terms to be accepted first.
    // The button is also disabled in this state, but guard here too so a manual
    // submit (e.g. Enter key) can't bypass it.
    if (!consentSatisfied) {
      setError('Please confirm you understand the priced-by-weight pre-order terms to continue.');
      return;
    }

    setProcessing(true);
    setError(null);

    // Compose the shipping address only for delivery orders.
    const shippingAddress = isDelivery
      ? [formData.address, formData.city, formData.state, formData.zipCode]
          .filter(Boolean).join(', ')
      : null;

    try {
      // 1) Create the split-cart checkout server-side. The server re-fetches the
      //    catalog (authoritative price/tax/sold_by), partitions the cart into a
      //    deterministic orders row (+ PaymentIntent) and/or a packaged preorders
      //    row, computes tax + shipping, and returns a client_secret only when
      //    there is something to charge now. X-Tenant-ID + optional customer
      //    Bearer are attached by the api layer; we add the Idempotency-Key.
      const checkoutRes = await api.post('/site/checkout', {
        items: cartItems.map(item => ({
          item_id: item.id,
          quantity: item.quantity,
        })),
        customer: {
          email: formData.email,
          name: `${formData.firstName} ${formData.lastName}`.trim(),
          phone: formData.phone,
        },
        fulfillment_type: deliveryMethod, // 'pickup' | 'delivery'
        shipping_address: shippingAddress,
        notes: formData.deliveryNotes || null,
      }, {
        headers: { 'Idempotency-Key': getIdempotencyKey() },
      });

      const checkoutBody = await checkoutRes.json();
      if (!checkoutRes.ok) {
        setError(checkoutBody?.message || 'We could not start your checkout. Please try again.');
        setProcessing(false);
        return;
      }

      const { cart_group_id: cartGroupId, order_id: orderId, client_secret: clientSecret } =
        checkoutBody.data || {};

      // 2) If there is a charge due (deterministic items), confirm the card
      //    payment inline against the returned PaymentIntent. A packaged-only
      //    cart returns no client_secret — skip the card step entirely. (The
      //    server is authoritative: even if the UI thought there was nothing to
      //    charge, the presence/absence of client_secret decides the card step.)
      if (clientSecret) {
        if (!stripe || !elements) {
          setError('Payment is still loading. Please wait a moment and try again.');
          setProcessing(false);
          return;
        }
        const { error: stripeError, paymentIntent } = await stripe.confirmCardPayment(clientSecret, {
          payment_method: {
            card: elements.getElement(CardElement),
            billing_details: {
              name: `${formData.firstName} ${formData.lastName}`.trim(),
              email: formData.email,
              phone: formData.phone,
              address: {
                line1: formData.address,
                city: formData.city,
                state: formData.state,
                postal_code: formData.zipCode,
                country: 'US',
              },
            },
          },
        });

        if (stripeError) {
          // Card declined / validation: keep the same idempotency key so the
          // retry reuses the existing cart_group + PaymentIntent.
          setError(stripeError.message);
          setProcessing(false);
          return;
        }
        if (paymentIntent && paymentIntent.status !== 'succeeded') {
          setError(`Payment could not be completed (status: ${paymentIntent.status}).`);
          setProcessing(false);
          return;
        }
      }

      // 3) Finalize: the confirm callback is the authoritative paid transition
      //    server-side (re-reads the PI from Stripe). For a packaged-only cart
      //    it is a no-op success. Best-effort — the connect webhook is a backstop.
      try {
        await api.post(`/site/checkout/${cartGroupId}/confirm`, {});
      } catch (confirmErr) {
        // Non-fatal: payment already succeeded; the webhook will reconcile.
      }

      setSucceeded(true);
      idempotencyKeyRef.current = null; // fresh key for any future order
      onSuccess(orderId || cartGroupId);
      navigate('/order-confirmation', {
        state: {
          orderId: orderId || null,
          cartGroupId,
          email: formData.email,
          deliveryMethod,
          packagedOnly: !clientSecret,
        },
      });
    } catch (err) {
      setError('An error occurred. Please try again.');
      setProcessing(false);
    }
  };

  return (
    <form className="checkout-form" onSubmit={handleSubmit}>
      {/* Contact Information */}
      <section className="checkout-section">
        <h2>Contact Information</h2>
        <div className="form-group">
          <label htmlFor="email">Email *</label>
          <input
            type="email"
            id="email"
            name="email"
            value={formData.email}
            onChange={handleChange}
            required
            placeholder="your@email.com"
          />
        </div>
        <div className="form-row">
          <div className="form-group">
            <label htmlFor="firstName">First Name *</label>
            <input
              type="text"
              id="firstName"
              name="firstName"
              value={formData.firstName}
              onChange={handleChange}
              required
            />
          </div>
          <div className="form-group">
            <label htmlFor="lastName">Last Name *</label>
            <input
              type="text"
              id="lastName"
              name="lastName"
              value={formData.lastName}
              onChange={handleChange}
              required
            />
          </div>
        </div>
        <div className="form-group">
          <label htmlFor="phone">Phone *</label>
          <input
            type="tel"
            id="phone"
            name="phone"
            value={formData.phone}
            onChange={handleChange}
            required
            placeholder="(903) 555-1234"
          />
        </div>
      </section>

      {/* Delivery Method */}
      <section className="checkout-section">
        <h2>Delivery Method</h2>
        <div className="delivery-options">
          <label className={`delivery-option ${!isDelivery ? 'selected' : ''}`}>
            <input
              type="radio"
              name="deliveryMethod"
              value="pickup"
              checked={!isDelivery}
              onChange={handleDeliveryMethodChange}
            />
            <div className="option-content">
              <span className="option-title">Farm Pickup</span>
              <span className="option-subtitle">
                {businessName ? `Free - Pick up at ${businessName}` : 'Free - Pickup'}
              </span>
              {pickupAddress && <span className="option-detail">{pickupAddress}</span>}
            </div>
          </label>
          <label className={`delivery-option ${isDelivery ? 'selected' : ''}`}>
            <input
              type="radio"
              name="deliveryMethod"
              value="delivery"
              checked={isDelivery}
              onChange={handleDeliveryMethodChange}
            />
            <div className="option-content">
              <span className="option-title">Local Delivery</span>
              <span className="option-subtitle">
                Delivered to your address
              </span>
              <span className="option-detail">Local delivery area</span>
            </div>
          </label>
        </div>
      </section>

      {/* Delivery Address (shown only for delivery) */}
      {isDelivery && (
        <section className="checkout-section">
          <h2>Delivery Address</h2>
          <div className="form-group">
            <label htmlFor="address">Street Address *</label>
            <input
              type="text"
              id="address"
              name="address"
              value={formData.address}
              onChange={handleChange}
              required={isDelivery}
            />
          </div>
          <div className="form-row form-row-3">
            <div className="form-group">
              <label htmlFor="city">City *</label>
              <input
                type="text"
                id="city"
                name="city"
                value={formData.city}
                onChange={handleChange}
                required={isDelivery}
              />
            </div>
            <div className="form-group">
              <label htmlFor="state">State *</label>
              <select
                id="state"
                name="state"
                value={formData.state}
                onChange={handleChange}
                required={isDelivery}
              >
                <option value="">Select…</option>
                {US_STATES.map(([code, label]) => (
                  <option key={code} value={code}>{label}</option>
                ))}
              </select>
            </div>
            <div className="form-group">
              <label htmlFor="zipCode">ZIP Code *</label>
              <input
                type="text"
                id="zipCode"
                name="zipCode"
                value={formData.zipCode}
                onChange={handleChange}
                required={isDelivery}
                maxLength={5}
              />
            </div>
          </div>
          <div className="form-group">
            <label htmlFor="deliveryNotes">Delivery Notes (Optional)</label>
            <textarea
              id="deliveryNotes"
              name="deliveryNotes"
              value={formData.deliveryNotes}
              onChange={handleChange}
              rows={3}
              placeholder="Gate code, special instructions, etc."
            />
          </div>
        </section>
      )}

      {/* Payment — only when there is a charge due now (deterministic items).
          A packaged-only cart is billed after the producer weighs each item, so
          no card is collected at checkout (the server returns no client_secret
          and the inline confirm is skipped). */}
      {hasDeterministic ? (
        <section className="checkout-section">
          <h2>Payment</h2>
          <div className="card-element-container">
            <CardElement options={cardStyle} />
          </div>
          <p className="payment-security">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <rect x="3" y="11" width="18" height="11" rx="2" ry="2"/>
              <path d="M7 11V7a5 5 0 0 1 10 0v4"/>
            </svg>
            Your payment information is encrypted and secure.
          </p>
        </section>
      ) : (
        <section className="checkout-section">
          <h2>Payment</h2>
          <p className="payment-weighed-note">
            Your order is priced by weight. Nothing is charged now — we'll weigh
            your items and send a final total when your order is ready.
          </p>
        </section>
      )}

      {/* Pre-order terms acceptance — only when the cart has priced-by-weight
          items. The customer must explicitly agree before placing the order.
          Client-side gate only (not persisted server-side). */}
      {hasPackaged && (
        <section className="checkout-section">
          <h2>Pre-order terms</h2>
          <label className={`preorder-consent ${acceptedPreorder ? 'accepted' : ''}`}>
            <input
              type="checkbox"
              checked={acceptedPreorder}
              onChange={(e) => setAcceptedPreorder(e.target.checked)}
            />
            <span className="preorder-consent-text">
              I understand my package (priced-by-weight) items are placed as a pre-order.
              They are weighed after I order, and {businessName || 'the seller'} will
              email me a secure payment link for the final amount.
              {hasDeterministic
                ? ' I am not charged for these package items now.'
                : ' I am not charged at checkout.'}
            </span>
          </label>
        </section>
      )}

      {/* Error Display */}
      {error && (
        <div className="checkout-error">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <circle cx="12" cy="12" r="10"/>
            <line x1="12" y1="8" x2="12" y2="12"/>
            <line x1="12" y1="16" x2="12.01" y2="16"/>
          </svg>
          {error}
        </div>
      )}

      {/* Submit Button — "Pay $X" only when there is a charge due now; otherwise
          a plain "Place Order" for the packaged-weight (billed-later) flow.
          Disabled until a packaged cart's pre-order terms are accepted. */}
      <button
        type="submit"
        className="btn btn-primary btn-lg btn-checkout-submit"
        disabled={processing || succeeded || !consentSatisfied}
      >
        {processing
          ? 'Processing...'
          : (hasDeterministic ? `Pay $${chargeTotal.toFixed(2)}` : 'Place Order')}
      </button>
    </form>
  );
}

// Main Checkout Page Component
function Checkout() {
  const { cartItems, clearCart } = useCart();
  const site = useSite();
  const navigate = useNavigate();

  // Split the cart into the two server-mirrored buckets (see isPackagedItem).
  // Deterministic = charge now; packaged = price-at-weighing (billed later).
  const deterministicItems = useMemo(
    () => cartItems.filter(item => !isPackagedItem(item)),
    [cartItems]
  );
  const packagedItems = useMemo(
    () => cartItems.filter(isPackagedItem),
    [cartItems]
  );
  const hasDeterministic = deterministicItems.length > 0;
  const hasPackaged = packagedItems.length > 0;

  // Stripe.js instance, memoized on the tenant's connected-account id. The
  // storefront's PaymentIntents are created ON the connected account
  // (siteCheckoutService), so Stripe.js must carry { stripeAccount } for the
  // inline confirmCardPayment to resolve the PI on the right account — otherwise
  // Stripe returns "No such payment_intent" (Feature C C-4 fix). When the tenant
  // has no connected account, fall back to a plain platform-key instance: card
  // entry still renders, and the server returns a 400 explaining card payments
  // aren't set up (so we never silently mis-route a charge).
  const stripeAccountId = site.stripeAccountId || null;
  const stripePromise = useMemo(() => {
    return stripeAccountId
      ? loadStripe(STRIPE_PUBLISHABLE_KEY, { stripeAccount: stripeAccountId })
      : loadStripe(STRIPE_PUBLISHABLE_KEY);
  }, [stripeAccountId]);

  // Tenant tax rate (B9 reuse) with a safe fallback when unconfigured.
  const taxRate = Number.isFinite(site.taxRate) && site.taxRate !== null
    ? site.taxRate
    : FALLBACK_TAX_RATE;
  const taxPercentLabel = (taxRate * 100).toFixed(taxRate * 100 % 1 === 0 ? 0 : 2);

  // Delivery method is the single source of truth here (parent), so the order
  // summary and the form's selection share one value. The form updates it via
  // onDeliveryMethodChange.
  const [deliveryMethod, setDeliveryMethod] = useState('pickup');

  // Charged-now money is computed over the DETERMINISTIC bucket ONLY, mirroring
  // the server (which charges tax + shipping on deterministic lines and never
  // includes packaged-weight lines in the PaymentIntent amount). Packaged lines
  // show "Price at weighing" with no number and are excluded from every figure.
  const chargeSubtotal = useMemo(
    () => deterministicItems.reduce(
      (total, item) => total + deterministicLineTotal(item), 0),
    [deterministicItems]
  );
  // Shipping is resolved over deterministic lines only (server parity).
  const shipping = hasDeterministic
    ? resolveShipping(deliveryMethod, deterministicItems, site.shippingRates)
    : 0;
  const chargeTax = useMemo(
    () => deterministicItems.reduce(
      (total, item) => total + deterministicLineTax(item), 0),
    [deterministicItems]
  );
  const taxableSubtotal = useMemo(
    () => deterministicItems.reduce(
      (total, item) => total + (item.is_taxable ? deterministicLineTotal(item) : 0), 0),
    [deterministicItems]
  );
  const chargeTotal = chargeSubtotal + shipping + chargeTax;

  // Non-binding estimate of PACKAGE (catch-weight) lines only. Bulk is charged
  // now with an exact total and is excluded from this figure.
  const weighedEstimateTotal = useMemo(
    () => packagedItems.reduce((sum, item) => {
      const e = computeLineEstimate(item, item.quantity);
      return e.hasEstimate ? sum + e.lineEstimate : sum;
    }, 0),
    [packagedItems]
  );
  const weighedEstimateCount = useMemo(
    () => packagedItems.filter(item => computeLineEstimate(item, item.quantity).hasEstimate).length,
    [packagedItems]
  );
  const hasWeighedEstimate = weighedEstimateCount > 0;
  const weighedEstimatePartial = hasWeighedEstimate && weighedEstimateCount < packagedItems.length;

  useEffect(() => {
    // Redirect to cart if empty
    if (cartItems.length === 0) {
      navigate('/cart');
    }
  }, [cartItems, navigate]);

  const handleSuccess = (orderRef) => {
    clearCart();
  };

  if (cartItems.length === 0) {
    return null;
  }

  return (
    <div className="checkout-page">
      <section className="checkout-hero">
        <div className="checkout-hero-content">
          <h1>Checkout</h1>
        </div>
      </section>

      <section className="checkout-content section">
        <div className="container">
          <div className="checkout-grid">
            {/* Checkout Form */}
            <div className="checkout-main">
              <Elements stripe={stripePromise}>
                <CheckoutForm
                  cartItems={cartItems}
                  hasDeterministic={hasDeterministic}
                  hasPackaged={hasPackaged}
                  chargeTotal={chargeTotal}
                  deliveryMethod={deliveryMethod}
                  onDeliveryMethodChange={setDeliveryMethod}
                  site={site}
                  onSuccess={handleSuccess}
                />
              </Elements>
            </div>

            {/* Order Summary */}
            <div className="checkout-sidebar">
              <div className="order-summary">
                <h2>Order Summary</h2>

                {/* Charged-now (deterministic) lines, with prices. */}
                {hasDeterministic && (
                  <div className="summary-items">
                    {deterministicItems.map(item => {
                      const isBulk = isBulkMode(item);
                      const uom = unitLabel(item.unit_of_measure);
                      return (
                      <div key={item.itemId} className="summary-item">
                        <div className="item-image">
                          <img src={item.image} alt={item.name} />
                          <span className="item-quantity" title={isBulk ? `Weight (${uom})` : undefined}>
                            {isBulk ? `${item.quantity} ${uom}` : item.quantity}
                          </span>
                        </div>
                        <div className="item-details">
                          <h4>{item.name}</h4>
                          {item.variant && <p className="variant">{item.variant}</p>}
                          {isBulk && (
                            <p className="summary-item-bulk-note">
                              {formatMoney(item.price)}/{uom} × {item.quantity} {uom}
                            </p>
                          )}
                        </div>
                        <div className="item-price">
                          ${deterministicLineTotal(item).toFixed(2)}
                        </div>
                      </div>
                    );})}
                  </div>
                )}

                {/* Packaged-weight lines: priced at weighing, no number, never
                    part of the charged total. */}
                {hasPackaged && (
                  <div className="summary-items summary-items-weighed">
                    <p className="weighed-group-label">Package items (billed later)</p>
                    {packagedItems.map(item => {
                      const est = computeLineEstimate(item, item.quantity);
                      const estLabel = lineEstimateLabel(item, item.quantity);
                      return (
                        <div key={item.itemId} className="summary-item summary-item-weighed">
                          <div className="item-image">
                            <img src={item.image} alt={item.name} />
                            <span className="item-quantity">{item.quantity}</span>
                          </div>
                          <div className="item-details">
                            <h4>{item.name}</h4>
                            {item.variant && <p className="variant">{item.variant}</p>}
                          </div>
                          <div className="item-price item-price-tbd">
                            {est.hasEstimate
                              ? <span title={estLabel}>est. {formatMoney(est.lineEstimate)}</span>
                              : 'Price at weighing'}
                          </div>
                        </div>
                      );
                    })}
                    <p className="weighed-group-note">
                      These package items are placed as a pre-order and weighed before
                      fulfillment. You'll receive a secure payment link for the
                      final amount by email, and aren't charged for them now.
                    </p>
                    {hasWeighedEstimate && (
                      <div className="summary-row summary-row-weighed-estimate">
                        <span>Estimated weighed items{weighedEstimatePartial ? ' (partial)' : ''}</span>
                        <span>~{formatMoney(weighedEstimateTotal)}</span>
                      </div>
                    )}
                  </div>
                )}

                {/* Totals: charged-now figures only. Omitted entirely for a
                    packaged-only cart (nothing is charged at checkout). */}
                {hasDeterministic && (
                  <div className="summary-totals">
                    <div className="summary-row">
                      <span>Subtotal</span>
                      <span>${chargeSubtotal.toFixed(2)}</span>
                    </div>
                    <div className="summary-row">
                      <span>Shipping</span>
                      <span>{shipping > 0 ? `$${shipping.toFixed(2)}` : 'Free'}</span>
                    </div>
                    <div className="summary-row">
                      <span>Tax{taxableSubtotal > 0 ? ` (${taxPercentLabel}%)` : ''}</span>
                      <span>${chargeTax.toFixed(2)}</span>
                    </div>
                    <div className="summary-row summary-row-total">
                      <span>Total{hasPackaged ? ' due now' : ''}</span>
                      <span>${chargeTotal.toFixed(2)}</span>
                    </div>
                  </div>
                )}

                {!hasDeterministic && (
                  <div className="summary-totals">
                    <div className="summary-row summary-row-total">
                      <span>Due now</span>
                      <span>$0.00</span>
                    </div>
                  </div>
                )}

                <Link to="/cart" className="edit-cart-link">
                  ← Edit Cart
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}

export default Checkout;
